import { Text } from '@/components/text';
import { formatProductPlanTariff } from '@/lib/utils/format-product-plan-tariff';

import type { EnergyType } from '@energy-retailer/constants';
import type { FeedInRatesSchema, RatesSchema } from '@energy-retailer/schema';

type TariffInformationProps = {
  planName: string;
  controlledLoad: boolean;
  rates?: RatesSchema;
  feedInRates: FeedInRatesSchema;
  energyType: EnergyType;
  includeSolarFeedTariff?: boolean;
};

const tariffLabel: Record<keyof RatesSchema, string> = {
  supply: 'Daily supply charge',
  singleRate: 'Single rate tariff',
  peak: 'Peak tariff',
  shoulder: 'Shoulder tariff',
  offPeak: 'Off Peak tariff',
  superOffPeak: 'Super Off Peak tariff',
  summerDemand: 'Summer Demand tariff',
  nonSummerDemand: 'Non Summer Demand tariff',
  peakDemand: 'Peak Demand tariff',
  default: 'Solar feed-in tariff',
  premium: 'Premium feed-in tariff',
  controlledLoadPeak: 'Controlled load peak tariff',
  controlledLoadShoulder: 'Controlled load shoulder tariff',
  controlledLoadOffPeak: 'Controlled load off-peak tariff',
  controlledLoadSupply: 'Controlled load supply charge',
  controlledLoad: 'Controlled load tariff',
  controlledLoadSupply1: 'Controlled load 1 supply charge',
  controlledLoad1: 'Controlled load 1 tariff',
  controlledLoadSupply2: 'Controlled load 2 supply charge',
  controlledLoad2: 'Controlled load 2 tariff',
};

export const TariffInformation = ({
  planName,
  rates = {} as RatesSchema,
  feedInRates = {} as FeedInRatesSchema,
  energyType,
  includeSolarFeedTariff = true,
}: TariffInformationProps) => {
  return Object.entries({ superOffPeak: 0, ...rates, ...feedInRates }).map(
    ([key, value]) => {
      const label = tariffLabel[key as keyof RatesSchema];

      if (!includeSolarFeedTariff && key === 'default') {
        return null;
      }

      if (key === 'premium') {
        return null; // We do not display premium feed-in tariffs: https://app.clickup.com/t/86cug4n9x
      }

      // We do not display (incl GST) for feed-in tariffs: https://app.clickup.com/t/86cuvh52p
      const incGST = key === 'default' ? '' : '(incl GST)';

      const showZero =
        (planName === 'EV Day' || planName === 'EV Day Saver') &&
        key === 'superOffPeak';

      if (!value && !showZero) {
        return null;
      }

      return (
        <div
          className="flex justify-between"
          key={key}
        >
          <Text
            variant="body2"
            weight="semi"
          >
            {label} {incGST}
          </Text>
          <Text variant="body2">
            {formatProductPlanTariff({
              tariff: Number(value),
              type: energyType,
              supply: [
                'supply',
                'controlledLoadSupply',
                'controlledLoadSupply1',
                'controlledLoadSupply2',
              ].includes(key),
              showZero,
            })}
          </Text>
        </div>
      );
    }
  );
};
